import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { sendGTMEvent } from '@/utils/analytics';

import ShortcutButton from '@/components/layouts/ShortcutButton';
import { useGlobal } from '@/contexts/GlobalContext';
import { useDrawer } from '@/contexts/DrawerContext';
import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import { useTacticalboardDrawer } from '@/contexts/TacticalboardDrawerContext';
import ExpiredTicketModalContent from '@/components/tickets/ExpiredTicketModalContent';
import InformationModalContent from '@/components/layouts/InformationModalContent';
import FootballlogDrawer from '@/components/footballlogs/FootballlogDrawer';
import GoalDrawer from '@/components/goals/GoalDrawer';
import TacticalboardDrawer from '@/components/tacticalboard/TacticalboardDrawer';
import { formatYYYYmmWithHyphen } from '@/utils/date';
import { Menu } from '@/types/menu';
import { getCurrentMenu } from '@/utils/getCurrentMenu';

export default function BottomMenu() {
  const location = useLocation();
  /**
   * react context
   */
  const context = useGlobal();
  const { openDrawer, closeDrawer } = useTacticalboardDrawer();
  const { openDrawer: openNewNoteDrawer } = useDrawer();
  const { openConfirmModal } = useConfirmModal();

  /**
   * useState
   */
  const [currentMenu, setCurrentMenu] = useState<Menu | undefined>(undefined);

  // サッカーノートカレンダーのデフォルト日付を設定
  let date: Date;
  if (context.systemDate != null) {
    date = new Date(context.systemDate);
  } else {
    date = new Date();
  }
  const systemDateYearMonth = formatYYYYmmWithHyphen(date);

  /**
   * ドロワー表示（サッカーボード）
   */
  const handleOpenDrawer = () => {
    sendGTMEvent('openTacticalboardv2');
    openDrawer(<TacticalboardDrawer onCloseDrawer={closeDrawer} />);
  };

  /**
   * ドロワー表示（サッカーノート新規）
   */
  const handleOpenNewNoteDrawerByShortcut = () => {
    // FeatureFlag制御: 機能OFF時は有効期限チェックしない
    if (context.isFeatureEnabled('ticket_payment')) {
      // チームプランのみ適用中の場合
      if (context.user?.has_team_plan_only) {
        openConfirmModal(
          <InformationModalContent>
            チームプラン適用中です。
            <br />
            チームページにて、ノート作成が可能です。
          </InformationModalContent>
        );
        return;
      }

      // チケット有効期限切れの場合
      if (!context.user?.has_ticket) {
        openConfirmModal(<ExpiredTicketModalContent />);
        return;
      }
    }
    openNewNoteDrawer(<FootballlogDrawer />);
  };

  /**
   * ドロワー表示（目標新規）
   */
  const handleOpenNewGoalsDrawerByShortcut = () => {
    // FeatureFlag制御: 機能OFF時は有効期限チェックしない
    if (context.isFeatureEnabled('ticket_payment')) {
      // 課金機能にアクセスできない場合
      // MyGoalsはチケット未適用かつチームプラン未適用（どちらかが適用されていればアクセス可）の場合アラート。
      if (!context.user?.has_ticket && !context.user?.has_team_plan) {
        openConfirmModal(<ExpiredTicketModalContent />);
        return;
      }
    }
    openNewNoteDrawer(<GoalDrawer />);
  };

  /**
   * ショートカットボタンを表示するかどうか
   */
  const showShortcut = () => {
    return currentMenu === 'HOME' || currentMenu === 'NOTE' || currentMenu === 'GOAL' ? true : false;
  };

  useEffect(() => {
    const menu = getCurrentMenu(location.pathname);
    setCurrentMenu(menu);
  }, [location.pathname]);

  return (
    <nav>
      <ul className="relative select-none pb-safe list-none flex items-center justify-between bg-white border-t border-green-500">
        <li className="flex-grow min-h-12 h-12 flex justify-center items-center">
          <Link
            to="/home"
            className={`${currentMenu === 'HOME' ? 'text-green-500' : 'text-gray-400'} rounded-full transition duration-300 active:scale-90`}>
            <svg
              className="w-8 h-8"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"
              />
            </svg>
          </Link>
        </li>
        <li className="flex-grow min-h-12 h-12 flex justify-center items-center">
          <Link
            to={`/notes/calendar/${systemDateYearMonth}`}
            className={`${currentMenu === 'NOTE' ? 'text-green-500' : 'text-gray-400'} rounded-full transition duration-300 active:scale-90`}>
            <svg
              className="w-8 h-8"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6.03v13m0-13c-2.819-.831-4.715-1.076-8.029-1.023A.99.99 0 0 0 3 6v11c0 .563.466 1.014 1.03 1.007 3.122-.043 5.018.212 7.97 1.023m0-13c2.819-.831 4.715-1.076 8.029-1.023A.99.99 0 0 1 21 6v11c0 .563-.466 1.014-1.03 1.007-3.122-.043-5.018.212-7.97 1.023"
              />
            </svg>
          </Link>
        </li>
        <li className="flex-grow min-h-12 h-12 flex justify-center items-center">
          <Link
            to="/goals"
            className={`${currentMenu === 'GOAL' ? 'text-green-500' : 'text-gray-400'} rounded-full transition duration-300 active:scale-90`}>
            <svg
              className="w-8 h-8"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 4h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3m0 3h6m-6 7 2 2 4-4m-5-9v4h4V3h-4Z"
              />
            </svg>
          </Link>
        </li>
        <li className="flex-grow min-h-12 h-12 flex justify-center items-center">
          <Link
            to="/supporters"
            className={`${currentMenu === 'SUPPORTER' ? 'text-green-500' : 'text-gray-400'} rounded-full transition duration-300 active:scale-90`}>
            <svg
              className="w-8 h-8"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                d="M4.5 17H4a1 1 0 0 1-1-1 3 3 0 0 1 3-3h1m0-3.05A2.5 2.5 0 1 1 9 5.5M19.5 17h.5a1 1 0 0 0 1-1 3 3 0 0 0-3-3h-1m0-3.05a2.5 2.5 0 1 0-2-4.45m.5 13.5h-7a1 1 0 0 1-1-1 3 3 0 0 1 3-3h3a3 3 0 0 1 3 3 1 1 0 0 1-1 1Zm-1-9.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z"
              />
            </svg>
          </Link>
        </li>
        <li className="flex-grow min-h-12 h-12 flex justify-center items-center">
          <div
            onClick={handleOpenDrawer}
            className="text-gray-400 rounded-full transition duration-300 active:scale-90">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 12H20M5 21H19C19.5523 21 20 20.5523 20 20V4C20 3.44772 19.5523 3 19 3H5C4.44772 3 4 3.44772 4 4V20C4 20.5523 4.44772 21 5 21ZM10 6H14C14.5523 6 15 5.55228 15 5V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V5C9 5.55228 9.44772 6 10 6ZM10 21H14C14.5523 21 15 20.5523 15 20V19C15 18.4477 14.5523 18 14 18H10C9.44772 18 9 18.4477 9 19V20C9 20.5523 9.44772 21 10 21ZM15 12C15 13.6569 13.6569 15 12 15C10.3432 15 9 13.6569 9 12C9 10.3432 10.3432 9 12 9C13.6569 9 15 10.3432 15 12Z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </div>
        </li>
        <li className="flex-grow min-h-12 h-12 flex justify-center items-center">
          <Link
            to="/videos"
            className={`${currentMenu === 'VIDEO' ? 'text-green-500' : 'text-gray-400'} rounded-full transition duration-300 active:scale-90`}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M21.7 8.03694C21.6064 7.32632 21.335 6.6508 20.911 6.07294C20.3855 5.54358 19.6728 5.24221 18.927 5.23394C16.16 5.03394 12.001 5.03394 12.001 5.03394C12.001 5.03394 7.84398 5.03394 5.07298 5.23394C4.32738 5.24204 3.61498 5.54346 3.08998 6.07294C2.66409 6.65024 2.39221 7.32649 2.29998 8.03794C2.17638 9.10229 2.10962 10.1725 2.09998 11.2439V12.7439C2.10957 13.8154 2.17633 14.8856 2.29998 15.9499C2.39398 16.6619 2.66398 17.3399 3.08398 17.9219C3.68798 18.4579 4.46398 18.7589 5.27098 18.7699C6.85398 18.9209 12.002 18.9699 12.002 18.9699C12.002 18.9699 16.163 18.9699 18.93 18.7699C19.6762 18.7612 20.3892 18.4595 20.915 17.9299C21.3381 17.3514 21.6087 16.6756 21.702 15.9649C21.8256 14.9006 21.8924 13.8304 21.902 12.7589V11.2429C21.8907 10.1715 21.8233 9.10134 21.7 8.03694Z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M15 12L9.75 14.5981V9.40192L15 12Z" fill="currentColor" />
            </svg>
          </Link>
        </li>
      </ul>
      {showShortcut() && (
        <div className="absolute -top-20 right-4">
          <ShortcutButton
            onCreateNote={handleOpenNewNoteDrawerByShortcut}
            onCreateGoal={handleOpenNewGoalsDrawerByShortcut}
          />
        </div>
      )}
    </nav>
  );
}
